/**
 * VariantMatrix — task × variant heatmap for a group.
 *
 * Rows are tasks, columns the union of variant labels seen across the
 * group's runs, each cell one MODEL RUN's score for that (task, variant)
 * pair. Cell intensity scales with score; pairs the picked run never
 * covered render dashed. Row labels navigate to the all-variant task page,
 * cells to a task + variant history view.
 *
 * The header picker chooses which model run the cells read, defaulting to
 * the newest — training adds runs to this same grid rather than new
 * columns, so stepping back through them is the point. Reference runs are
 * pickable too, under their own heading, so a baseline's per-task scores are
 * visible in the same grid; they're never the default. Columns stay the full
 * union across every run so the grid never reflows between picks.
 *
 * Runs pushed without a variant land in a "default (unlabeled)" column,
 * so a group that never uses variants still renders as a single column.
 *
 * With only ONE variant there is no second axis left to preserve, and a
 * one-column matrix is just a stack of numbers with no shared baseline —
 * nothing is comparable at a glance. That case renders as a horizontal bar
 * graph instead (same cells, same run picker, rows ranked longest first).
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { DraftMarker, NewMarker } from '@/components/evals/DraftMarker'
import { isNewTask } from '@/components/evals/newTask'
import { formatAxisTick, formatScore, formatVariant, type ScoreFormat } from '@/components/evals/format'
import { modelRuns, pointForRun, type ModelRun } from '@/components/evals/modelRun'
import { HistoryPlot } from '@/components/evals/ScoreHistoryChart'
import { toChartData } from '@/components/evals/climb'
import { scalePosition, scoreCeiling, scoreTicks } from '@/components/evals/scale'
import { groupVariantPath, taskVariantPath } from '@/components/evals/taskVariant'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { EvalTaskSummary, ReferenceLine, ScorePoint } from '@/api/generated/model'

interface VariantMatrixProps {
  groupSlug: string
  tasks: EvalTaskSummary[]
  /** When set, render only this variant's column for tasks that have it. */
  variant?: string
  /** How to render cell scores — percent (default) or decimal for C-index/AUC. */
  format?: ScoreFormat
}

/**
 * Pastel diverging ramp: hue rotates red (zero) -> yellow -> green (the
 * group's ceiling). Deliberately a literal scale rather than semantic tokens —
 * --success is a 12%-saturation olive, so any token mix reads gray at the top
 * of the ramp. Alpha keeps it pastel over the card surface in both themes.
 *
 * The ramp reads against the group's ceiling, not its observed range, so the
 * tint means the same thing in every group: how far up full scale this score
 * sits. A group scoring 0.976-0.992 tints as the near-perfect set it is,
 * rather than painting its weakest 97.6% pure red for being last.
 */
function cellStyle(score: number, ceiling: number): React.CSSProperties {
  // Desaturated, fixed-lightness whisper tint (VISUAL_DESIGN_PRINCIPLES §3):
  // the printed score carries the info; the tint only hints at it.
  const hue = scalePosition(score, ceiling) * 130
  return { backgroundColor: `hsl(${hue.toFixed(0)} 40% 58% / 0.35)` }
}

/**
 * Every score the matrix can show, across all tasks, variants, and runs —
 * the basis for the group's ceiling.
 *
 * Read off the FULL history rather than the picked run, so the ceiling (and so
 * every cell's colour) holds still while you step through the run picker.
 */
function allScores(tasks: EvalTaskSummary[]): number[] {
  return tasks.flatMap((task) =>
    task.variant_scores.flatMap((cell) => [
      ...cell.history.map((point) => point.score),
      ...(cell.reference_history ?? []).map((point) => point.score),
    ])
  )
}

/**
 * The hover card behind a cell or a bar: what the picked run scored, and where
 * that sits on the pair's FULL scored history. References join the plot as
 * levels (TRA-993) — on a small chart they become bars beside the run and the
 * gap sketch names the distance to the leader — but they never join the climb.
 */

/** One reference level per model — its newest run (`reference_history` is oldest first). */
function referenceLevels(references: ScorePoint[]): ReferenceLine[] {
  const latest = new Map<string, number>()
  for (const point of references) latest.set(point.model, point.score)
  return [...latest.entries()].map(([label, score]) => ({ label, score }))
}

function ScoreTooltip({
  heading,
  score,
  history,
  references,
  picked,
  format,
  trials,
}: {
  heading: string
  score: number
  history: ScorePoint[]
  /** The pair's reference runs (`VariantScore.reference_history`). */
  references: ScorePoint[]
  picked: ModelRun
  format: ScoreFormat
  /** Per-trial case rows behind this score; null when the push carried none. */
  trials: number | null
}): React.JSX.Element {
  return (
    <TooltipContent className="space-y-1">
      <p>
        {heading}: {formatScore(score, format)}
        {trials !== null && ` over ${trials.toString()} ${trials === 1 ? 'trial' : 'trials'}`} on{' '}
        {picked.label}
        {picked.isReference
          ? ' (reference)'
          : `, of ${history.length.toString()} ${history.length === 1 ? 'run' : 'runs'}`}{' '}
        (click for history)
      </p>
      <div className="w-64">
        <HistoryPlot
          data={toChartData(history)}
          format={format}
          hoverable={false}
          referenceLines={referenceLevels(references)}
        />
      </div>
    </TooltipContent>
  )
}

/**
 * Bar-track gridlines: a hairline at the start of every quarter of the track.
 * The far edge isn't a tile start, so the track carries it as a right border —
 * without it a full-length bar has nothing to read as full.
 */
const TRACK_GRID: React.CSSProperties = {
  backgroundImage: 'linear-gradient(to right, hsl(var(--border)) 0 1px, transparent 1px)',
  backgroundSize: '25% 100%',
}

/** Quarter stops along the track. The gridlines are fixed; their LABELS follow
 *  the ceiling, so a rate keeps 0 / 0.25 / 0.5 / 0.75 / 1. */
const TICK_COUNT = 5

/** End ticks hug the track edges; the rest center on their gridline. */
function tickStyle(fraction: number): React.CSSProperties {
  if (fraction === 0) return { left: 0 }
  if (fraction === 1) return { right: 0 }
  return { left: `${(fraction * 100).toString()}%`, transform: 'translateX(-50%)' }
}

/**
 * Horizontal bar graph — the single-variant rendering of the matrix data.
 *
 * Rows rank longest-first because comparing tasks is the entire reason to draw
 * bars; a task the picked run never covered has no length to rank, so it sinks
 * to the bottom as a dashed empty track (same convention as a dashed cell).
 * The whole track is the link, so a near-zero bar is still clickable.
 */
/** Bar-view row orders. Score sorts keep uncovered tasks at the bottom and
 * break ties by name; name sorts are pure alpha, coverage ignored. */
type BarSort = 'score-desc' | 'score-asc' | 'name-asc' | 'name-desc'

const BAR_SORTS: { value: BarSort; label: string }[] = [
  { value: 'score-desc', label: 'Highest score first' },
  { value: 'score-asc', label: 'Lowest score first' },
  { value: 'name-asc', label: 'Name A to Z' },
  { value: 'name-desc', label: 'Name Z to A' },
]

function TaskScoreBars({
  groupSlug,
  tasks,
  variant,
  picked,
  format,
  sort,
  ceiling,
}: {
  groupSlug: string
  tasks: EvalTaskSummary[]
  variant: string
  picked: ModelRun
  format: ScoreFormat
  sort: BarSort
  ceiling: number
}): React.JSX.Element {
  const rows = tasks
    .map((task) => {
      const cell = task.variant_scores.find((entry) => entry.variant === variant)
      return { task, cell, point: cell ? pointForRun(cell, picked) : undefined }
    })
    .sort((left, right) => {
      const byName = left.task.display_name.localeCompare(right.task.display_name)
      if (sort === 'name-asc') return byName
      if (sort === 'name-desc') return -byName
      if ((left.point === undefined) !== (right.point === undefined)) {
        return left.point === undefined ? 1 : -1
      }
      const delta = (right.point?.score ?? 0) - (left.point?.score ?? 0)
      const byScore = sort === 'score-asc' ? -delta : delta
      return byScore !== 0 ? byScore : byName
    })

  return (
    <div
      className="grid items-center gap-x-3 gap-y-1"
      style={{ gridTemplateColumns: 'minmax(0, max-content) minmax(0, 1fr) max-content' }}
    >
      {rows.map(({ task, cell, point }) => (
        <div key={task.id} className="contents">
          <span className="flex min-w-0 items-center gap-2">
            <Link
              to={`/evals/groups/${groupSlug}/tasks/${task.slug}`}
              className={`truncate text-sm hover:text-primary hover:underline ${
                task.status === 'draft' ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              {task.display_name}
            </Link>
            {task.status === 'draft' && <DraftMarker prUrl={task.pr_url} />}
            {task.status === 'active' && isNewTask(task.created_at) && <NewMarker />}
          </span>
          {cell === undefined || point === undefined ? (
            <span
              aria-label={`${task.display_name}: no ${picked.label} run`}
              className="h-8 rounded border border-dashed border-border"
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={taskVariantPath({ groupSlug, taskSlug: task.slug, variant })}
                  aria-label={`${task.display_name}: ${formatScore(point.score, format)}, view history`}
                  style={TRACK_GRID}
                  className="flex h-8 min-w-0 items-center rounded border-r border-border hover:outline hover:outline-2 hover:-outline-offset-2 hover:outline-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <span
                    aria-hidden="true"
                    className="h-full rounded"
                    style={{
                      width: `${(scalePosition(point.score, ceiling) * 100).toFixed(1)}%`,
                      ...cellStyle(point.score, ceiling),
                    }}
                  />
                </Link>
              </TooltipTrigger>
              <ScoreTooltip
                heading={variant === '' ? task.display_name : `${task.display_name} · ${variant}`}
                score={point.score}
                history={cell.history}
                references={cell.reference_history ?? []}
                picked={picked}
                format={format}
                trials={point.cases ?? null}
              />
            </Tooltip>
          )}
          <span className="text-right font-mono text-xs tabular-nums text-foreground">
            {formatScore(point?.score ?? null, format)}
          </span>
        </div>
      ))}
      {/* Scale under the bar column only — the label and value columns keep
          their width by sitting this row out. */}
      <span aria-hidden="true" />
      <div aria-hidden="true" className="relative mt-1 h-4">
        {scoreTicks(ceiling, TICK_COUNT).map((tick, index) => (
          <span
            key={tick}
            className="absolute font-mono text-xs text-muted-foreground"
            style={tickStyle(index / (TICK_COUNT - 1))}
          >
            {formatAxisTick(tick, format)}
          </span>
        ))}
      </div>
      <span aria-hidden="true" />
    </div>
  )
}

export function VariantMatrix({ groupSlug, tasks: allTasks, variant, format = 'percent' }: VariantMatrixProps): React.JSX.Element | null {
  // Every run in the group, newest first — derived off the FULL task list so
  // the pick set doesn't shrink under a `?variant=` scope.
  const runs = modelRuns(allTasks)
  const [selected, setSelected] = useState<string | null>(null)
  const [sort, setSort] = useState<BarSort>('score-desc')
  // Off the FULL task list, for the same reason as `runs`: a `?variant=` scope
  // narrows what's drawn, never what the colours and the track mean.
  const ceiling = scoreCeiling(allScores(allTasks))
  const tasks =
    variant === undefined
      ? allTasks
      : allTasks.filter((t) => t.variant_scores.some((v) => v.variant === variant))
  // Column order is the API's cell order (first run that used the label), not
  // alphabetical — a Set preserves insertion, so the union just accumulates.
  const variants =
    variant === undefined
      ? Array.from(new Set(tasks.flatMap((t) => t.variant_scores.map((v) => v.variant))))
      : [variant]
  // Null default (and a stale pick after refetch) falls back to the newest run
  // without a state write — same self-healing trick as the chart's task picker.
  // `modelRuns` puts every scored run ahead of every reference, so the fallback
  // is a real datapoint unless the group has nothing but baselines.
  const picked = runs.find((run) => run.key === selected) ?? runs[0]
  const scoredRuns = runs.filter((run) => !run.isReference)
  const referenceRuns = runs.filter((run) => run.isReference)
  const grouped = scoredRuns.length > 0 && referenceRuns.length > 0
  if (variants.length === 0 || tasks.length === 0 || picked === undefined) return null
  // One variant means one column: there's no second axis to hold, so bars
  // (which give every task a shared baseline) beat a 1-D grid of numbers.
  const soleVariant = variants.length === 1 ? variants[0] : undefined

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Bars have no column header to hang the `?variant=` drill-in on, so
              the title carries it — unless we're already inside that view. The
              variant is NOT in the title: a group measuring one thing tends to
              name it "Score", which would read "Score score by task". */}
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {soleVariant === undefined ? (
              'Score by task × variant'
            ) : variant === undefined ? (
              <Link
                to={groupVariantPath({ groupSlug, variant: soleVariant })}
                className="hover:text-primary hover:underline"
              >
                Score by task
              </Link>
            ) : (
              'Score by task'
            )}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
          {/* Bars only: the matrix keeps its fixed row order so columns
              stay comparable across picks. */}
          {soleVariant !== undefined && (
            <Select
              value={sort}
              onValueChange={(next) => {
                setSort(next as BarSort)
              }}
            >
              <SelectTrigger aria-label="Task order" className="h-8 w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BAR_SORTS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Shown even with a single run: the label names which model the
              scores belong to, which the matrix otherwise never states.
              Headings only appear once there's something in both groups — a
              lone "Model runs" caption is noise. */}
          <Select
            value={picked.key}
            onValueChange={(next) => {
              setSelected(next)
            }}
          >
            <SelectTrigger
              aria-label={
                soleVariant === undefined
                  ? 'Model run shown in the matrix'
                  : 'Model run shown in the chart'
              }
              className="h-8 w-56 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[
                { heading: 'Model runs', options: scoredRuns },
                { heading: 'References', options: referenceRuns },
              ]
                .filter((section) => section.options.length > 0)
                .map((section) => (
                  <SelectGroup key={section.heading}>
                    {grouped && <SelectLabel>{section.heading}</SelectLabel>}
                    {section.options.map((run) => (
                      <SelectItem key={run.key} value={run.key}>
                        {run.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
            </SelectContent>
          </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* An unlabeled sole variant goes unmentioned — "default (unlabeled)"
            carries no information and only crowds the sentence. */}
        <p className="mb-2 text-xs text-muted-foreground">
          {soleVariant === undefined
            ? `Each cell is ${picked.label}'s score for that task and variant`
            : `Each bar is ${picked.label}'s score for that task${soleVariant === '' ? '' : `, variant ${soleVariant}`}`}
          {picked.isReference ? ' (a reference run)' : ''}.
        </p>
        {soleVariant !== undefined ? (
          <TaskScoreBars
            groupSlug={groupSlug}
            tasks={tasks}
            variant={soleVariant}
            picked={picked}
            format={format}
            sort={sort}
            ceiling={ceiling}
          />
        ) : (
        <div className="overflow-x-auto">
          <div
            className="grid gap-0.5"
            style={{
              gridTemplateColumns: `max-content repeat(${variants.length.toString()}, minmax(3.5rem, 1fr))`,
            }}
          >
            <span aria-hidden="true" />
            {variants.map((variant) => (
              <Link
                key={variant}
                to={groupVariantPath({ groupSlug, variant })}
                className="truncate pb-1 text-center font-mono text-xs text-muted-foreground hover:text-primary hover:underline"
              >
                {formatVariant(variant)}
              </Link>
            ))}
            {tasks.map((task) => {
              const byVariant = new Map(task.variant_scores.map((v) => [v.variant, v]))
              return (
                <div key={task.id} className="contents">
                  <span className="flex min-h-11 min-w-0 items-center gap-2 pr-3">
                    <Link
                      to={`/evals/groups/${groupSlug}/tasks/${task.slug}`}
                      className={`truncate text-sm hover:text-primary hover:underline ${
                        task.status === 'draft' ? 'text-muted-foreground' : 'text-foreground'
                      }`}
                    >
                      {task.display_name}
                    </Link>
                    {task.status === 'draft' && <DraftMarker prUrl={task.pr_url} />}
                    {task.status === 'active' && isNewTask(task.created_at) && <NewMarker />}
                  </span>
                  {variants.map((variant) => {
                    const cell = byVariant.get(variant)
                    // Dashed when this pair has no runs at all, or when the
                    // picked run simply didn't cover it — a push only fans out
                    // across the tasks it evaluated.
                    const point = cell ? pointForRun(cell, picked) : undefined
                    if (!cell || !point) {
                      return (
                        <span
                          key={variant}
                          aria-label={`${task.display_name} ${formatVariant(variant)}: no ${picked.label} run`}
                          className="mb-0.5 flex min-h-11 items-center justify-center rounded border border-dashed border-border text-xs text-muted-foreground"
                        >
                          —
                        </span>
                      )
                    }
                    return (
                      <Tooltip key={variant}>
                        <TooltipTrigger asChild>
                          <Link
                            to={taskVariantPath({
                              groupSlug,
                              taskSlug: task.slug,
                              variant,
                            })}
                            aria-label={`${task.display_name} · ${formatVariant(variant)}: ${formatScore(point.score, format)}, view history`}
                            style={cellStyle(point.score, ceiling)}
                            className="mb-0.5 flex min-h-11 items-center justify-center rounded text-xs font-medium tabular-nums text-foreground hover:outline hover:outline-2 hover:-outline-offset-2 hover:outline-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            {formatScore(point.score, format)}
                          </Link>
                        </TooltipTrigger>
                        {/* The trend stays the pair's FULL scored history, not
                            just the picked run — the point of hovering is
                            seeing where this run sits among the others. */}
                        <ScoreTooltip
                          heading={`${task.display_name} · ${formatVariant(variant)}`}
                          score={point.score}
                          history={cell.history}
                          references={cell.reference_history ?? []}
                          picked={picked}
                          format={format}
                          trials={point.cases ?? null}
                        />
                      </Tooltip>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  )
}
